import React, { useCallback, useEffect, useMemo, useState, ChangeEvent, KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import { ViewIconButton } from "components/DatatablesActionIcons";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from "@tanstack/react-table";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { DesktopTableLoader, MobileCardsLoader, DataTableStatus } from "components/DataTableLoader";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { ApprovalStatusBadge, RequestBadge } from "components/ApprovalStatusBadge";
import { getAuthCookie } from "../../utils/authCookies";
import {
  fetchModulePermissionList,
  isTeamMemberUser,
  MODULE_PERMISSION_NAMES,
} from "../../utils/moduleActionPermissions";

export type ChangeRequestRow = Record<string, unknown> & { id?: string; _id?: string };

const columnHelper = createColumnHelper<ChangeRequestRow>();

const STATUS_OPTIONS = [
  { value: "", label: "Select status..." },
  { value: "PENDING", label: "Pending" },
  { value: "PROCESSING", label: "Processing" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

const TYPE_OPTIONS = [
  { value: "", label: "Select type..." },
  { value: "office", label: "Offices" },
  { value: "agent", label: "Agents" },
  { value: "team_member", label: "Teammembers" },
];

const TYPE_LABELS: Record<string, string> = {
  office: "Offices",
  agent: "Agents",
  team_member: "Teammembers",
};

const TYPE_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  office: { bg: "#e0f0ff", color: "#276ada" },
  agent: { bg: "#e0cfff", color: "#6b21a8" },
  team_member: { bg: "#e0f7fa", color: "#00838f" },
};

function sentenceLabel(value: string): string {
  const raw = value.trim().toLowerCase();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function titleFromKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
      }
    }
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((item) => displayValue(item)).join(", ");
  }
  if (typeof value === "object") {
    const row = value as Record<string, unknown>;
    const named = row.name ?? row.officeName ?? row.office_name ?? row.label ?? row.email;
    if (named != null && String(named).trim()) return String(named);
    const parts = Object.entries(row)
      .filter(([, v]) => v != null && v !== "" && typeof v !== "object")
      .map(([k, v]) => `${titleFromKey(k)}: ${displayValue(v)}`);
    return parts.length ? parts.join(" · ") : "—";
  }
  return String(value);
}

function textFromRow(row: ChangeRequestRow, keys: string[]): string {
  const sources: Record<string, unknown>[] = [row];
  const changes = row.changes;
  if (changes && typeof changes === "object" && !Array.isArray(changes)) {
    sources.push(changes as Record<string, unknown>);
  }
  for (const source of sources) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }
  return "";
}

function resourceType(row: ChangeRequestRow): string {
  return String(row.resource_type ?? "").trim().toLowerCase();
}

function officeName(row: ChangeRequestRow): string {
  if (resourceType(row) !== "office") return "—";
  return String(row.resource_name ?? "").trim() || "—";
}

function userName(row: ChangeRequestRow): string {
  const type = resourceType(row);
  if (type !== "agent" && type !== "team_member") return "—";
  const combined = `${textFromRow(row, ["firstName", "first_name", "firstname"])} ${textFromRow(row, ["lastName", "last_name", "lastname"])}`.trim();
  return combined || String(row.resource_name ?? "").trim() || "—";
}

function personEmail(row: ChangeRequestRow): string {
  return textFromRow(row, ["resource_email", "email", "Email"]) || "—";
}

function actorName(value: unknown): string {
  if (!value || typeof value !== "object") return "—";
  const name = String((value as Record<string, unknown>).name ?? "").trim();
  return name || "—";
}

export function requestId(row: ChangeRequestRow): string {
  return String(row.id || row._id || row.request_id || "").trim();
}

function changeRequestViewPath(pathname: string, id: string): string {
  const base = pathname.replace(/\/view\/[^/]+\/?$/, "").replace(/\/$/, "");
  return `${base}/view/${encodeURIComponent(id)}`;
}

export function storeChangeRequestRow(row: ChangeRequestRow) {
  const id = requestId(row);
  if (!id) return;
  try {
    sessionStorage.setItem(`brokerage-change-request:${id}`, JSON.stringify(row));
  } catch {
    // Ignore storage failures; navigation state still carries the row.
  }
}

const SignageBrokerageChangeRequests = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isVendorRequests = location.pathname.includes("/vendorRequests");
  const pageTitle = isVendorRequests ? "Vendor requests" : "Brokerage Requests";
  const token = getAuthCookie("sessionToken");
  const organizationId = String(getAuthCookie("organizationId") || "").trim();
  const [rows, setRows] = useState<ChangeRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusDraft, setStatusDraft] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [typeDraft, setTypeDraft] = useState("");
  const [appliedResourceType, setAppliedResourceType] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [decision, setDecision] = useState<{
    id: string;
    status: "APPROVED" | "REJECTED";
    note: string;
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Brokerage owner can always approve; team members need the "approve" permission.
  const isTeamMember = isTeamMemberUser();
  const [canApprove, setCanApprove] = useState(isVendorRequests && !isTeamMember);

  useEffect(() => {
    if (!isVendorRequests) {
      setCanApprove(false);
      return;
    }
    if (!isTeamMember) {
      setCanApprove(true);
      return;
    }
    const controller = new AbortController();
    setCanApprove(false);
    fetchModulePermissionList(MODULE_PERMISSION_NAMES.brokerage_change_requests, { signal: controller.signal })
      .then((actions) => setCanApprove(actions.includes("approve") || actions.includes("review")))
      .catch(() => {
        if (!controller.signal.aborted) setCanApprove(false);
      });
    return () => controller.abort();
  }, [isVendorRequests, isTeamMember]);

  useEffect(() => {
    if (!token || !organizationId) {
      setLoading(false);
      setRows([]);
      setRowCount(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${encodeURIComponent(organizationId)}/brokerage-change-requests/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                ...(appliedStatus ? { status: appliedStatus } : {}),
                ...(appliedResourceType ? { resource_type: appliedResourceType } : {}),
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
            }),
          }
        );
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            (typeof result?.error === "string" && result.error) ||
              (typeof result?.message === "string" && result.message) ||
              "Failed to load brokerage requests."
          );
        }
        if (cancelled) return;
        const list = Array.isArray(result?.data) ? result.data : [];
        setRows(list);
        setRowCount(typeof result?.total === "number" ? result.total : list.length);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setRowCount(0);
          showError(err instanceof Error ? err.message : "Failed to load brokerage requests.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, organizationId, pagination.pageIndex, pagination.pageSize, globalFilter, appliedStatus, appliedResourceType, reloadKey]);

  const submitDecision = useCallback(async () => {
    if (!token || !decision?.id) return;
    const note = decision.note.trim();
    if (!note) {
      showError("Please enter a note.");
      return;
    }
    setDeciding(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/brokerage-change-requests/${encodeURIComponent(decision.id)}/decision`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: decision.status,
            decision_note: note,
          }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          (typeof result?.error === "string" && result.error) ||
            (typeof result?.message === "string" && result.message) ||
            "Failed to update the request."
        );
      }
      showSuccess(decision.status === "APPROVED" ? "Request approved." : "Request rejected.");
      setDecision(null);
      setReloadKey((key) => key + 1);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to update the request.");
    } finally {
      setDeciding(false);
    }
  }, [token, decision]);

  const columns = useMemo<ColumnDef<ChangeRequestRow, any>[]>(() => {
    const openView = (row: ChangeRequestRow) => {
      const id = requestId(row);
      if (!id) return;
      storeChangeRequestRow(row);
      navigate(changeRequestViewPath(location.pathname, id), { state: { request: row } });
    };

    const cols: ColumnDef<ChangeRequestRow, any>[] = [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text text-center S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm text-center S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.display({
        id: "resource_email",
        header: () => <div className="th-text">Resource Email</div>,
        cell: (info) => <div className="text-sm">{personEmail(info.row.original)}</div>,
      }),
      columnHelper.display({
        id: "user_name",
        header: () => <div className="th-text">User Name</div>,
        cell: (info) => <div className="text-sm">{userName(info.row.original)}</div>,
      }),
      columnHelper.display({
        id: "office_name",
        header: () => <div className="th-text">Office Name</div>,
        cell: (info) => <div className="text-sm">{officeName(info.row.original)}</div>,
      }),
      columnHelper.display({
        id: "operation",
        header: () => <div className="th-text">Operation</div>,
        cell: (info) => (
          <div className="text-sm">{sentenceLabel(String(info.row.original.operation || "")) || "—"}</div>
        ),
      }),
      columnHelper.display({
        id: "resource_type",
        header: () => <div className="th-text">Type</div>,
        cell: (info) => {
          const raw = String(info.row.original.resource_type ?? "").trim().toLowerCase();
          if (!raw) return <div className="text-sm">—</div>;
          const colors = TYPE_BADGE_COLORS[raw] || { bg: "#E2E3E5", color: "#383D41" };
          return (
            <div className="text-sm">
              <RequestBadge label={TYPE_LABELS[raw] || sentenceLabel(raw)} bg={colors.bg} color={colors.color} />
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "status",
        header: () => <div className="th-text">Status</div>,
        cell: (info) => (
          <div className="text-sm">
            <ApprovalStatusBadge status={info.row.original.status} />
          </div>
        ),
      }),
      columnHelper.display({
        id: "decision_note",
        header: () => <div className="th-text">Decision Note</div>,
        cell: (info) => <div className="text-sm">{displayValue(info.row.original.decision_note)}</div>,
      }),
      columnHelper.display({
        id: "cancelled_by",
        header: () => <div className="th-text">Cancelled By</div>,
        cell: (info) => <div className="text-sm">{actorName(info.row.original.cancelled_by)}</div>,
      }),
    ];

    if (isVendorRequests) {
      cols.push(
        columnHelper.display({
          id: "vendor_name",
          header: () => <div className="th-text">Vendor Name</div>,
          cell: (info) => <div className="text-sm">{displayValue(info.row.original.vendor_name)}</div>,
        })
      );
    }

    cols.push(
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text text-center">Actions</div>,
        cell: (info) => {
          const row = info.row.original;
          const id = requestId(row);
          const status = String(row.status || "").trim().toUpperCase();
          const showDecision = canApprove && id && status === "PENDING";
          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              <ViewIconButton title="View" onClick={() => openView(row)} />
              {showDecision ? (
                <>
                  <button
                    type="button"
                    className="export-button border-0"
                    style={{ padding: "6px 16px", fontSize: 14, lineHeight: 1.4 }}
                    disabled={deciding}
                    onClick={() => setDecision({ id, status: "APPROVED", note: "Approved" })}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="brokerage-reject-btn"
                    style={{ padding: "6px 16px", borderRadius: 8, fontSize: 14, lineHeight: 1.4, fontWeight: 500 }}
                    disabled={deciding}
                    onClick={() => setDecision({ id, status: "REJECTED", note: "" })}
                  >
                    Reject
                  </button>
                </>
              ) : null}
            </div>
          );
        },
      })
    );

    return cols;
  }, [pagination.pageIndex, pagination.pageSize, deciding, isVendorRequests, canApprove, location.pathname, navigate]);

  const table = useReactTable({
    data: rows,
    columns,
    pageCount: Math.max(1, Math.ceil(rowCount / pagination.pageSize)),
    state: { pagination, globalFilter },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="content position-relative">
      <div className="content container">
        <div className="po-header mb-4 shadow-border-datatables">
          <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
            <div className="po-title-wrap me-2 d-flex align-items-center">
              <div>
                <h4 className="po-title mb-1">{pageTitle}</h4>
                <div className="po-subtitle">Office change requests</div>
              </div>
            </div>
            <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
              <div className="input-group po-search">
                <span className="input-group-text">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search requests"
                  value={searchInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                      setGlobalFilter(searchInput);
                    }
                  }}
                  autoComplete="off"
                />
              </div>
              <button
                type="button"
                className={`po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-controls="brokerage-request-filters"
              >
                <i className="ri-filter-3-line me-2" />
                <span>Filters</span>
                <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
          </div>
          <div id="brokerage-request-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
            <div className="po-filters-bar d-flex">
              <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
                <div className="col-12 col-md-auto">
                  <label htmlFor="changeRequestType" className="form-label mb-1 label-1">Type</label>
                  <select
                    id="changeRequestType"
                    className="form-select"
                    value={typeDraft}
                    onChange={(e) => setTypeDraft(e.target.value)}
                  >
                    {TYPE_OPTIONS.map((option) => (
                      <option key={option.value || "all-types"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-auto">
                  <label htmlFor="changeRequestStatus" className="form-label mb-1 label-1">Status</label>
                  <select
                    id="changeRequestStatus"
                    className="form-select"
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton"
                    onClick={() => {
                      setAppliedStatus(statusDraft);
                      setAppliedResourceType(typeDraft);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn back-btn surface-btn fw-bold fs-16"
                    onClick={() => {
                      setStatusDraft("");
                      setAppliedStatus("");
                      setTypeDraft("");
                      setAppliedResourceType("");
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
          <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
            <h5 className="mb-0">{pageTitle}</h5>
          </div>
          <hr className="mb-2" style={{ borderTop: "none" }} />
          <div className="rdt-desktop">
            <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
              <table className="table production-table align-middle mb-0 table-1">
                {loading ? (
                  <DesktopTableLoader table={table} message="Loading brokerage requests…" variant="dots" />
                ) : (
                  <DesktopTableParts table={table} />
                )}
              </table>
            </div>
          </div>
          <div className="rdt-mobile">
            {loading ? (
              <MobileCardsLoader message="Loading brokerage requests…" variant="dots" />
            ) : (
              <MobileCardsOnly table={table} idColId="serial" titleColId="status" actionColId="actions" />
            )}
          </div>
        </div>
        <DataTableStatus loading={loading} isEmpty={table.getRowModel().rows.length === 0}>
          <div className="mt-4 mb-4">
            <TablePaginationControls table={table} />
          </div>
        </DataTableStatus>
        {decision ? (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(15, 23, 42, 0.45)", zIndex: 2000 }}
            onClick={() => {
              if (!deciding) setDecision(null);
            }}
          >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content border-0 shadow roboto-new" style={{ borderRadius: 16 }}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fs-19 fw-bold mb-0">
                    {decision.status === "APPROVED" ? "Approve request" : "Reject request"}
                  </h5>
                  <button type="button" className="btn-close" disabled={deciding} onClick={() => setDecision(null)} />
                </div>
                <div className="modal-body pt-3">
                  <label className="form-label roboto-new mb-1">Note</label>
                  <textarea
                    className="form-control roboto-new"
                    rows={3}
                    value={decision.note}
                    onChange={(e) => setDecision((prev) => (prev ? { ...prev, note: e.target.value } : prev))}
                    placeholder={decision.status === "REJECTED" ? "Reason for rejection" : "Approved"}
                  />
                </div>
                <div className="modal-footer border-0 pt-0 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn back-btn surface-btn fw-bold fs-16"
                    disabled={deciding}
                    onClick={() => setDecision(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={
                      decision.status === "APPROVED"
                        ? "btn btn-primary save-btn surface-btn fw-bold fs-16"
                        : "btn brokerage-reject-btn surface-btn fw-bold fs-16"
                    }
                    disabled={deciding}
                    onClick={() => void submitDecision()}
                  >
                    {deciding ? "Saving..." : decision.status === "APPROVED" ? "Approve" : "Reject"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <style>
          {`
            .brokerage-reject-btn {
              background: #e7000b;
              border: none;
              color: #fff;
              box-shadow: 0 6px 12px rgba(231, 0, 11, 0.2);
            }
            .brokerage-reject-btn:hover:not(:disabled) {
              background: #c10007;
              color: #fff;
            }
            .brokerage-reject-btn:disabled {
              opacity: 0.75;
              cursor: not-allowed;
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default SignageBrokerageChangeRequests;
